import Dictionary from '@/types/dictionary';
import { PORTFOLIO_ASSET_ALLOCATION } from '../enums/AssetAllocation';
import { STOCK_ASSET_ALLOCATIONS, STOCK_REGION_ALLOCATIONS } from './constants';
import { LOCALE } from './i18n';
import Stock from '@/types/stock';
import LocalizedString from '@/types/localized-string';
import LocalizedAllocationDetails from '@/types/localized-allocation-details';
import InvestmentItem from '@/types/investment-item';
import { getValidatedCountry } from './domicile';

const DOMICILE_ALLOCATION: Dictionary<string> = {
  CAN: PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ,
  USA: PORTFOLIO_ASSET_ALLOCATION.US_EQ,
};

/**
 * Get the allocations object for a stock. Sets
 * @param stock
 */
export const getStockAllocations = (stock: Stock) => {
  const allocationKey = DOMICILE_ALLOCATION[stock.domicile] || PORTFOLIO_ASSET_ALLOCATION.FOREIGN_EQ;

  const defaultAllocations = Object.values(PORTFOLIO_ASSET_ALLOCATION).reduce((acc: Dictionary<number>, value) => {
    acc[value] = 0;
    return acc;
  }, {});

  return {
    ...defaultAllocations,
    [PORTFOLIO_ASSET_ALLOCATION.EQ]: 100,
    [allocationKey]: 100,
  };
};

/**
 * Gets the stock capitalization that are not equal to the value provided so that if the stock is considered Large cap
 * then we return an array of Small and Mid with 0 weights
 * @param {LocalizedString} capitalization The stock capitalization as a localized string
 * @returns {LocalizedAllocationDetails[]} an array of localized allocations excluding the capitalization provided
 */
const getOtherStockCapitalizations = (capitalization: LocalizedString): LocalizedAllocationDetails[] => {
  const value = capitalization[LOCALE.EN_CA] || '';
  return [
    {
      name: {
        [LOCALE.EN_CA]: 'Large',
        [LOCALE.FR_CA]: 'Grande',
      },
      weight: 0,
    },
    {
      name: {
        [LOCALE.EN_CA]: 'Mid',
        [LOCALE.FR_CA]: 'Moyenne',
      },
      weight: 0,
    },
    {
      name: {
        [LOCALE.EN_CA]: 'Small',
        [LOCALE.FR_CA]: 'Petite',
      },
      weight: 0,
    },
  ].filter(item => item.name[LOCALE.EN_CA] !== value);
};

/**
 * Transform custom allocation data to LocalizedAllocationDetails format
 */
export const formatCustomAllocationData = (customData: LocalizedAllocationDetails[] | undefined): LocalizedAllocationDetails[] | undefined => {
  if (!customData || !Array.isArray(customData)) {
    return undefined;
  }

  return customData.map(item => {
    const { name, weight, subAllocations } = item;

    return {
      name,
      weight,
      subAllocations: subAllocations?.map(sub => ({
        name: sub.name,
        weight: sub.weight,
      })),
    };
  });
};

const FULL_WEIGHT = 100;

export const stockToInvestmentItem = (stock: Stock): InvestmentItem => {
  const { assetType, capitalization, companyName, customData, domicile, distributionYield, _id, pbRatio, peRatio, industry, returnOnEquity } = stock;

  const assetAllocName = STOCK_ASSET_ALLOCATIONS[domicile] || STOCK_ASSET_ALLOCATIONS.OTHER;
  const country = getValidatedCountry(domicile);
  const region = STOCK_REGION_ALLOCATIONS[domicile] || STOCK_REGION_ALLOCATIONS.OTHER;
  const customAllocation = customData?.allocations?.asset?.data;

  const asset =
    !customAllocation || customAllocation.length === 0
      ? [{ name: { ...assetAllocName }, weight: FULL_WEIGHT }]
      : formatCustomAllocationData(customAllocation as LocalizedAllocationDetails[]);

  return {
    _id,
    assetType,
    allocations: {
      sector: [{ name: industry, weight: FULL_WEIGHT }],
      country: [{ name: country, weight: FULL_WEIGHT }],
      equityCountry: [{ name: country, weight: FULL_WEIGHT }],
      equityRegion: [{ name: region, weight: FULL_WEIGHT }],
      asset,
    },
    capitalization: capitalization && [
      {
        name: capitalization,
        weight: FULL_WEIGHT,
      },
      ...getOtherStockCapitalizations(capitalization),
    ],
    distributionYield,
    holdings: { list: [{ name: companyName, weight: FULL_WEIGHT }] },
    pbRatio,
    peRatio,
    returnOnEquity,
  };
};
